/* === file: /frontend/src/pages/GameAdmin.js === */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { socket } from '../socket';

import ic1800ad from '../assets/1800ad.png';
import icff66c4 from '../assets/ff66c4.png';
import ic38b6ff from '../assets/38b6ff.png';
import ic5bce21 from '../assets/5bce21.png';
import icf6c700 from '../assets/f6c700.png';
import ice62129 from '../assets/e62129.png';

const ICONS = { '1800ad': ic1800ad, 'ff66c4': icff66c4, '38b6ff': ic38b6ff, '5bce21': ic5bce21, 'f6c700': icf6c700, 'e62129': ice62129 };
const hexToIcon = (hex='') => ICONS[String(hex).replace('#','').toLowerCase()] || '';

const API = process.env.REACT_APP_API_BASE || '';
const ORDER = ['#1800ad','#ff66c4','#38b6ff','#5bce21','#f6c700','#e62129'].map(s=>s.toLowerCase());
const LINES = [
  [0,1,2,3], [4,5,6,7], [8,9,10,11], [12,13,14,15],
  [0,4,8,12],[1,5,9,13],[2,6,10,14],[3,7,11,15],
  [0,5,10,15],[3,6,9,12],
];

function cellIndexToLabel(i) {
  const n = Number(i);
  if (!Number.isFinite(n) || n < 0 || n > 15) return '';
  return String(n + 1);
}

function describePendingLine(lineCode) {
  const cells = String(lineCode || '')
    .split('-')
    .map(v => Number(v))
    .filter(v => Number.isFinite(v));
  if (cells.length < 2) return { dir: '', cellsLabel: '' };
  const a = cells[0];
  const d = cells[cells.length - 1];
  const ra = Math.floor(a / 4);
  const rd = Math.floor(d / 4), cd = d % 4, ca = a % 4;
  const dr = rd - ra, dc = cd - ca;
  let dir = '連線';
  if (dr === 0) dir = '橫列';
  else if (dc === 0) dir = '直行';
  else if (dr === dc) dir = '斜線 ╲';
  else if (dr === -dc) dir = '斜線 ╱';
  const cellsLabel = cells.map(cellIndexToLabel).join('-');
  return { dir, cellsLabel };
}

function orderCellBadges(teamsArr, lockTeamIds) {
  const raw = (Array.isArray(teamsArr) ? teamsArr : []).slice(0, 2);
  const locks = Array.isArray(lockTeamIds) ? lockTeamIds : [];
  const lockSet = new Set(locks.map((id) => Number(id)));
  const hasLock = (tid) => lockSet.has(Number(tid));
  const locked = raw.filter((tid) => hasLock(tid));
  const unlocked = raw.filter((tid) => !hasLock(tid));
  return [...locked, ...unlocked].slice(0, 2);
}

/**
 * 參數：
 *   /game-admin?hex=1800ad,ff66c4
 *   /game-admin?ids=1,2
 * → 只影響「可送出」的白名單；盤面顯示與 UI 版型一律相同。
 */
export default function GameAdmin(){
  const [loaded, setLoaded]   = useState(false);
  const [state, setState]     = useState({ occupancy:Array.from({ length: 16 }, () => []), cellLocks:Array.from({ length: 16 }, () => []), teams:[], claims:[], scores:{}, scoredLines:[], lastVictory:null });
  const [plays, setPlays]     = useState([]);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [openPlays, setOpenPlays] = useState(false);
  const [globalTimer, setGlobalTimer] = useState({ running:false, paused:false, startAt:null, endsAt:null, remainingMs:0, participatingTeamIds:null });
  const [tick, setTick]       = useState(0);
  const [selectedCell, setSelectedCell] = useState(null); // 選中的格子
  const [showTeamSelector, setShowTeamSelector] = useState(false); // 是否顯示隊伍選擇器
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [participatingTeamIds, setParticipatingTeamIds] = useState([]);
  const [controlGroups, setControlGroups] = useState([]);
  const [groupSlugInput, setGroupSlugInput] = useState('');
  const [groupDisplayName, setGroupDisplayName] = useState('');
  const [groupTeamPick, setGroupTeamPick] = useState(() => new Set());
  const [claimDecisionSecStr, setClaimDecisionSecStr] = useState('0.5');
  const [cellCapacity, setCellCapacity] = useState(2);
  const [showStartModal, setShowStartModal] = useState(false);
  const lastSessionRef = useRef({ durationMinutes: 60, participatingTeamIds: [] });

  // 頁籤標題：餓鬼夜行
  useEffect(() => {
    const prev = document.title;
    document.title = '餓鬼夜行';
    return () => { document.title = prev; };
  }, []);

  /* 解析參數（支援 hex 與 ids） */
  const param = useMemo(() => {
    const u = new URL(window.location.href);
    const hexStr = u.searchParams.get('hex');
    const idsStr = u.searchParams.get('ids');
    const hx = (hexStr ? hexStr.split(',') : []).map(s => s?.replace('#','').toLowerCase()).filter(Boolean);
    const ids = (idsStr ? idsStr.split(',') : []).map(v => Number(v)).filter(v => Number.isFinite(v));
    return { hx, ids };
  }, []);

  useEffect(() => {
    const onState    = (s) => setState({
      ...s,
      cellLocks: s.cellLocks || Array.from({ length: 16 }, () => []),
    });
    const onPlaysNew = () => reloadPlays();
    const onReset    = () => reloadAll();
    const onTimer    = (t) => setGlobalTimer(t);
    socket.on('game:state', onState);
    socket.on('plays:new', onPlaysNew);
    socket.on('game:reset', onReset);
    socket.on('game:timer', onTimer);
    const onSettings = (s) => {
      if (s && Number.isFinite(s.claimDecisionSeconds)) {
        setClaimDecisionSecStr(String(s.claimDecisionSeconds));
      }
    };
    socket.on('game:settings', onSettings);
    return () => {
      socket.off('game:state', onState);
      socket.off('plays:new', onPlaysNew);
      socket.off('game:reset', onReset);
      socket.off('game:timer', onTimer);
      socket.off('game:settings', onSettings);
    };
  }, []);

  useEffect(() => { reloadAll(); }, []);
  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/game/timer`); if (r.ok) setGlobalTimer(await r.json()); } catch {} })(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/game/settings`);
        if (!r.ok) return;
        const j = await r.json();
        if (Number.isFinite(j.claimDecisionSeconds)) setClaimDecisionSecStr(String(j.claimDecisionSeconds));
      } catch { /* 略過 */ }
    })();
  }, []);
  useEffect(() => { const t = setInterval(()=>setTick(x=>x+1), 1000); return () => clearInterval(t); }, []);

  const reloadControlGroups = async () => {
    try {
      const r = await fetch(`${API}/api/game/control-groups`);
      if (!r.ok) return;
      const j = await r.json();
      setControlGroups(j.groups || []);
    } catch { /* 表未部署時略過 */ }
  };
  useEffect(() => { reloadControlGroups(); }, []);

  const reloadAll = async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/api/game/state`).then(r => r.json()),
        fetch(`${API}/api/game/plays?top=200`).then(r => r.json()),
      ]);
      setState({ ...s, cellLocks: s.cellLocks || Array.from({ length: 16 }, () => []) }); setPlays(p.plays || []); setLoaded(true);
    } catch { setLoaded(true); }
  };
  const reloadPlays = async () => { try { const r = await fetch(`${API}/api/game/plays?top=200`); const j = await r.json(); setPlays(j.plays || []); } catch {} };

  /* 全部隊伍（排好序＋加 icon）；這是「渲染與檢視」使用來源，與參數無關 */
  const teamsAll = useMemo(() => {
    const arr = (state.teams || []).map(t => ({ ...t, __icon: hexToIcon(t.COLOR_HEX) }));
    return arr.sort((a,b)=>ORDER.indexOf(String(a.COLOR_HEX||'').toLowerCase())-ORDER.indexOf(String(b.COLOR_HEX||'').toLowerCase()));
  }, [state.teams]);

  useEffect(() => {
    if (!teamsAll.length) return;
    setParticipatingTeamIds((prev) => (prev.length ? prev : teamsAll.map((t) => t.TEAM_ID)));
  }, [teamsAll]);

  useEffect(() => {
    const pids = globalTimer.participatingTeamIds;
    if (pids && pids.length) setParticipatingTeamIds(pids);
  }, [globalTimer.participatingTeamIds]);

  useEffect(() => {
    if (globalTimer.cellCapacity) setCellCapacity(globalTimer.cellCapacity);
  }, [globalTimer.cellCapacity]);

  /* 參數白名單（只控制「可送出」） */
  const allowById  = useMemo(() => new Set(param.ids), [param]);
  const allowByHex = useMemo(() => new Set(param.hx.map(h => h.replace('#','').toLowerCase())), [param]);
  const isAllowedTeam = (t) => {
    if (!t) return false;
    const idOK  = allowById.size  ? allowById.has(Number(t.TEAM_ID)) : true;
    const hexOK = allowByHex.size ? allowByHex.has(String(t.COLOR_HEX||'').replace('#','').toLowerCase()) : true;
    return (allowById.size || allowByHex.size) ? (idOK && hexOK) : true; // 沒有參數＝全部允許
  };


  /* 倒數 top3（顯示於 UI） */
  const pendingClaims = useMemo(() => {
    const now = Date.now();
    const pend = (state.claims || []).filter(c => !c.status || c.status === 'pending');
    const list = pend.map(c => {
      const left = c.expiresAt ? Math.max(0, c.expiresAt - now) : Math.max(0, c.remainingMs || 0);
      return { ...c, left };
    });
    return list.sort((a,b)=> (a.left - b.left)).slice(0, 3);
  }, [state.claims, tick]);

  /* 點擊格子 */
  const handleCellClick = (cellIndex) => {
    if (!mayPlacePieces) return;
    setSelectedCell(cellIndex);
    setShowTeamSelector(true);
  };

  /* 選擇隊伍並送出（後端會擠掉未鎖定或較舊隊；兩隊皆鎖定時拒絕） */
  const handleTeamSelect = async (teamId) => {
    if (selectedCell == null || teamId == null) return;
    const t = teamsAll.find(x => x.TEAM_ID === teamId);
    if (!isAllowedTeam(t)) { 
      setMsg('此後台不允許操作該隊伍'); 
      setShowTeamSelector(false);
      setSelectedCell(null);
      return; 
    }
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/play`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ teamId, cell: selectedCell })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(j.message || j.error || '送出失敗');
        setShowTeamSelector(false);
        setSelectedCell(null);
        return;
      }
      setShowTeamSelector(false);
      setSelectedCell(null);
      await reloadAll();
    } catch (e) {
      setMsg('送出失敗，請重試');
    } finally { 
      setBusy(false); 
    }
  };

  const remove = async (id) => {
    if (!window.confirm('確定刪除此明細？將重算連線判定。')) return;
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/plays/${id}`, { method: 'DELETE' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setMsg(j.message || '刪除失敗');
        return;
      }
      await reloadAll();
    } catch {
      setMsg('刪除失敗');
    } finally {
      setBusy(false);
    }
  };

  const toggleParticipating = (tid) => {
    setParticipatingTeamIds((prev) => {
      const s = new Set(prev);
      if (s.has(tid)) s.delete(tid);
      else s.add(tid);
      return [...s];
    });
  };

  const selectAllParticipating = () => setParticipatingTeamIds(teamsAll.map((t) => t.TEAM_ID));
  const clearAllParticipating = () => setParticipatingTeamIds([]);

  const saveClaimSettings = async (opts = {}) => {
    const { silent = false } = opts;
    const sec = Number(claimDecisionSecStr);
    if (!Number.isFinite(sec) || sec <= 0) {
      if (!silent) setMsg('連線判定秒數須為正數');
      return false;
    }
    if (!silent) {
      setBusy(true);
      setMsg('');
    }
    try {
      const r = await fetch(`${API}/api/game/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claimDecisionSeconds: sec }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (!silent) setMsg(j.message || j.error || '連線判定期儲存失敗');
        return false;
      }
      if (Number.isFinite(j.claimDecisionSeconds)) setClaimDecisionSecStr(String(j.claimDecisionSeconds));
      if (!silent) setMsg('已儲存連線判定期');
      return true;
    } catch {
      if (!silent) setMsg('連線判定期儲存失敗');
      return false;
    } finally {
      if (!silent) setBusy(false);
    }
  };

  const openStartModal = () => {
    if (!participatingTeamIds.length && teamsAll.length) {
      setParticipatingTeamIds(teamsAll.map((t) => t.TEAM_ID));
    }
    setShowStartModal(true);
  };

  const confirmStartGame = async () => {
    const dm = Math.max(1, Math.min(48 * 60, Number(durationMinutes) || 60));
    const pSel = participatingTeamIds.filter((id) => teamsAll.some((t) => t.TEAM_ID === id));
    if (!pSel.length) {
      setMsg('請至少勾選一隊參加');
      return;
    }
    setBusy(true);
    setMsg('');
    const claimOk = await saveClaimSettings({ silent: true });
    if (!claimOk) {
      setBusy(false);
      return;
    }
    const participatingPayload = pSel.length >= teamsAll.length ? undefined : pSel;
    lastSessionRef.current = { durationMinutes: dm, participatingTeamIds: participatingPayload || teamsAll.map((t) => t.TEAM_ID) };
    try {
      const r = await fetch(`${API}/api/game/timer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start',
          durationMinutes: dm,
          cellCapacity,
          ...(participatingPayload ? { participatingTeamIds: participatingPayload } : {}),
        }),
      });
      const t = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(t.message || t.error || '開始失敗');
        return;
      }
      setGlobalTimer(t);
      setShowStartModal(false);
      await reloadAll();
    } catch (e) {
      setMsg('開始遊戲失敗，請重試');
    } finally {
      setBusy(false);
    }
  };

  const pauseGame = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/timer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'pause' }),
      });
      const t = await r.json().catch(() => ({}));
      if (!r.ok) setMsg(t.message || t.error || '暫停失敗');
      else setGlobalTimer(t);
    } catch {
      setMsg('暫停失敗');
    } finally {
      setBusy(false);
    }
  };

  const resumeGame = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/timer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resume' }),
      });
      const t = await r.json().catch(() => ({}));
      if (!r.ok) setMsg(t.message || t.error || '繼續失敗');
      else setGlobalTimer(t);
    } catch {
      setMsg('繼續失敗');
    } finally {
      setBusy(false);
    }
  };

  const restartGame = async () => {
    if (!window.confirm('確定重開一局？將清空盤面並以相同設定重新計時。')) return;
    const dm = lastSessionRef.current.durationMinutes || Math.max(1, Number(durationMinutes) || 60);
    const pSaved = lastSessionRef.current.participatingTeamIds;
    const pSel = (pSaved && pSaved.length ? pSaved : participatingTeamIds).filter((id) => teamsAll.some((t) => t.TEAM_ID === id));
    if (!pSel.length) {
      setMsg('請至少勾選一隊參加');
      return;
    }
    const participatingPayload = pSel.length >= teamsAll.length ? undefined : pSel;
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/timer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'restart',
          durationMinutes: dm,
          ...(participatingPayload ? { participatingTeamIds: participatingPayload } : {}),
        }),
      });
      const t = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(t.message || t.error || '重開失敗');
        return;
      }
      setGlobalTimer(t);
      await reloadAll();
    } catch {
      setMsg('重開失敗');
    } finally {
      setBusy(false);
    }
  };

  const copyGroupUrl = (slug) => {
    const path = `${window.location.origin.replace(/\/$/, '')}/game-admin/${encodeURIComponent(slug)}`;
    navigator.clipboard.writeText(path).then(() => setMsg('已複製連結')).catch(() => setMsg(path));
  };

  const saveNewGroup = async () => {
    const slug = String(groupSlugInput || '').trim();
    const teamIds = [...groupTeamPick];
    if (!slug) {
      setMsg('請填寫網址代號（例 group1）');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/control-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, displayName: groupDisplayName || null, teamIds }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(j.error === 'slug_exists' ? '代號已存在' : (j.message || '儲存失敗'));
        return;
      }
      setGroupSlugInput('');
      setGroupDisplayName('');
      setGroupTeamPick(new Set());
      await reloadControlGroups();
    } catch {
      setMsg('儲存失敗');
    } finally {
      setBusy(false);
    }
  };

  const deleteGroup = async (slug) => {
    if (!window.confirm(`刪除群組 ${slug}？`)) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/game/control-groups/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      await reloadControlGroups();
    } catch {
      setMsg('刪除失敗');
    } finally {
      setBusy(false);
    }
  };

  const toggleGroupTeamPick = (tid) => {
    setGroupTeamPick((prev) => {
      const n = new Set(prev);
      if (n.has(tid)) n.delete(tid);
      else n.add(tid);
      return n;
    });
  };

  const pendingKey = useMemo(() => new Set(pendingClaims.map(c => `${c.lineCode}|${c.teamId}`)), [pendingClaims]);

  const findTeamName = (id) => (state.teams||[]).find(t => t.TEAM_ID===id)?.TEAM_NAME;

  // 計算分數並排序（後端：佔格每格 +1，每條連線鎖定另加獎勵分）
  const teamScores = useMemo(() => {
    const scores = state.scores || {};
    return teamsAll.map(team => ({
      ...team,
      score: scores[team.TEAM_ID] || 0
    })).sort((a, b) => b.score - a.score);
  }, [teamsAll, state.scores]);

  const isClockCounting = useMemo(() => {
    const t = globalTimer;
    return !!(t.running && !t.paused && (t.remainingMs || 0) > 0);
  }, [globalTimer]);

  const mayPlacePieces = isClockCounting;

  const showGameEndedBanner = useMemo(() => {
    const t = globalTimer;
    if (t.paused) return false;
    return !t.running || (t.remainingMs || 0) <= 0;
  }, [globalTimer]);

  const matchStatusLabel = useMemo(() => {
    if (globalTimer.paused) return '已暫停';
    if (isClockCounting) return '進行中';
    if (globalTimer.running) return '進行中';
    if ((globalTimer.startAt || globalTimer.endsAt) && showGameEndedBanner) return '已結束';
    return '未開始';
  }, [globalTimer, isClockCounting, showGameEndedBanner]);

  const matchParticipatingTeams = useMemo(() => {
    const pids = globalTimer.participatingTeamIds;
    if (pids && pids.length) {
      return teamsAll.filter((t) => pids.includes(t.TEAM_ID));
    }
    if (globalTimer.running || globalTimer.startAt) return teamsAll;
    return [];
  }, [globalTimer, teamsAll]);

  const matchDurationLabel = useMemo(() => {
    if (globalTimer.startAt && globalTimer.endsAt) {
      const mins = Math.round((globalTimer.endsAt - globalTimer.startAt) / 60000);
      return `${mins} 分鐘`;
    }
    return '—';
  }, [globalTimer]);

  const selectorOcc = useMemo(() => {
    if (selectedCell == null) return [];
    return Array.isArray(state.occupancy?.[selectedCell]) ? state.occupancy[selectedCell] : [];
  }, [selectedCell, state.occupancy]);

  const selectorBothLocked = useMemo(() => {
    if (selectedCell == null || selectorOcc.length < 2) return false;
    const locks = state.cellLocks?.[selectedCell] || [];
    const ls = new Set(locks.map(Number));
    return selectorOcc.every((tid) => ls.has(Number(tid)));
  }, [selectedCell, selectorOcc, state.cellLocks]);

  const teamsSelectable = useMemo(() => {
    if (selectorBothLocked) return [];
    return teamsAll.filter((t) => isAllowedTeam(t) && !selectorOcc.includes(t.TEAM_ID));
  }, [teamsAll, selectorOcc, selectorBothLocked, allowById, allowByHex]);

  const cellLockEntries = useMemo(() => {
    const locks = state.cellLocks || [];
    const out = [];
    for (let i = 0; i < 16; i++) {
      for (const tid of locks[i] || []) {
        const n = Number(tid);
        const t = teamsAll.find((x) => Number(x.TEAM_ID) === n);
        out.push({ cell: i, teamId: n, teamName: t?.TEAM_NAME || `隊#${n}` });
      }
    }
    return out.sort((a, b) => a.cell - b.cell || a.teamId - b.teamId);
  }, [state.cellLocks, teamsAll]);

  const deleteCellLineScore = async (cell, teamId) => {
    const t = teamsAll.find((x) => Number(x.TEAM_ID) === Number(teamId));
    const name = t?.TEAM_NAME || `隊#${teamId}`;
    if (!window.confirm(
      `確定刪除格 ${cell + 1}「${name}」的顏色？\n將移除該格佔位並依盤面重新判定連線；若不再連線，同線其餘格的鎖定會解除。`
    )) return;
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/cell-locks`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cell, teamId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(j.message || j.error || '刪除失敗');
        return;
      }
      setMsg('已刪除並重新判定連線');
      await reloadAll();
    } catch {
      setMsg('刪除失敗');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-m-wrap">
      <style>{css}</style>

      {/* 頂部工具列與時鐘 */}
      <header className="topbar">
        <div className="title">連線系統後台</div>
        <div className="actions timer-actions">
          <button className="ghost" type="button" onClick={openStartModal} disabled={busy || isClockCounting}>開始新局</button>
          <button className="ghost" type="button" onClick={pauseGame} disabled={busy || !isClockCounting}>暫停</button>
          <button className="ghost" type="button" onClick={resumeGame} disabled={busy || !globalTimer.paused}>繼續</button>
          <button className="ghost" type="button" onClick={restartGame} disabled={busy}>重開</button>
        </div>
      </header>

      <div className="clock-wrap">
        <BigClock timer={globalTimer} />
        {showGameEndedBanner && (
          <div className="game-ended-banner">
            <span className="game-ended-text">遊戲已結束</span>
          </div>
        )}
        <section className="match-panel">
          <div className="match-panel-head">
            <span className="match-panel-title">本局比賽</span>
            <span className={`match-status ${matchStatusLabel === '進行中' ? 'on' : ''}`}>{matchStatusLabel}</span>
          </div>
          <div className="match-meta">
            <span>時長 {matchDurationLabel}</span>
            <span>格子上限 每格 {globalTimer.cellCapacity || 2} 隊</span>
            <span>連線判定 {claimDecisionSecStr} 秒</span>
          </div>
          <div className="match-teams">
            {matchParticipatingTeams.length ? (
              matchParticipatingTeams.map((t) => (
                <span key={t.TEAM_ID} className="match-team-chip" title={t.TEAM_NAME}>
                  <span className="match-dot" style={{ background: t.COLOR_HEX || '#444' }} />
                  {t.TEAM_NAME}
                </span>
              ))
            ) : (
              <span className="muted">尚未開始</span>
            )}
          </div>
        </section>
        {pendingClaims.length > 0 && (
          <div className="countdown-stack">
            {pendingClaims.map(c => (
              <MiniCountdown key={`${c.lineCode}|${c.teamId}`} claim={c} team={(state.teams||[]).find(t=>t.TEAM_ID===c.teamId)} />
            ))}
          </div>
        )}
      </div>

      {/* 盤面預覽：永遠用完整 teamsAll，與參數無關 */}
      <main className="content">
        <section className="panel">
          <h3 className="panel-title">小幫手負責組別（固定網址）</h3>
          <div className="group-form">
            <input className="g-inp" placeholder="網址代號（例 group1）" value={groupSlugInput} onChange={(e) => setGroupSlugInput(e.target.value)} disabled={busy} />
            <input className="g-inp" placeholder="顯示名稱（可選）" value={groupDisplayName} onChange={(e) => setGroupDisplayName(e.target.value)} disabled={busy} />
            <button type="button" className="ghost" onClick={saveNewGroup} disabled={busy}>新增群組</button>
          </div>
          <div className="group-team-pick">
            {teamsAll.map((t) => (
              <label key={t.TEAM_ID} className="part-chip">
                <input type="checkbox" checked={groupTeamPick.has(t.TEAM_ID)} onChange={() => toggleGroupTeamPick(t.TEAM_ID)} disabled={busy} />
                <span>{t.TEAM_NAME}</span>
              </label>
            ))}
          </div>
          <div className="group-list">
            {controlGroups.map((g) => {
              const namesLine = (g.teamIds || []).map((id) => findTeamName(id) || `隊#${id}`).join('、');
              return (
              <div key={g.slug} className="group-row">
                <div className="group-row-main">
                  <div className="g-line1">
                    <span className="g-slug">{g.slug}</span>
                    {g.displayName ? <span className="g-dn">{g.displayName}</span> : null}
                  </div>
                  <div className="g-teams-line" title={namesLine || undefined}>
                    <span className="g-teams-label">負責隊伍</span>
                    <span className="g-teams-names">{namesLine || '（尚未勾選隊伍）'}</span>
                  </div>
                </div>
                <div className="group-row-actions">
                  <button type="button" className="ghost tiny" onClick={() => copyGroupUrl(g.slug)}>複製連結</button>
                  <button type="button" className="link danger" onClick={() => deleteGroup(g.slug)} disabled={busy}>刪除</button>
                </div>
              </div>
            );})}
            {!controlGroups.length && <div className="muted">尚無群組（或資料表未就緒）</div>}
          </div>
        </section>

        {/* 分數面板 */}
        <section className="panel">
          <h3 className="panel-title">分數（佔格 +1；連線鎖定另加獎勵分）</h3>
          <div className="score-board score-board-row">
            {teamScores.map((team, idx) => (
              <div
                key={team.TEAM_ID}
                className={`score-chip-inline ${idx === 0 && team.score > 0 ? 'top-score' : ''}`}
                title={team.TEAM_NAME}
              >
                <span className="sc-rank">#{idx + 1}</span>
                <span className="sc-dot" style={{ background: team.COLOR_HEX || '#444' }} />
                <span className="sc-name">{team.TEAM_NAME}</span>
                <span className="sc-val">{team.score}</span>
              </div>
            ))}
            {teamScores.length === 0 && (
              <div className="score-empty">尚無分數</div>
            )}
          </div>
        </section>

        <section className="panel">
          <h3 className="panel-title">盤面預覽（點擊格子選擇隊伍）</h3>
          <div className="board">
            <Grid 
              occupancy={state.occupancy} 
              cellLocks={state.cellLocks && state.cellLocks.length === 16 ? state.cellLocks : Array.from({ length: 16 }, () => [])}
              teams={teamsAll} 
              pendingKey={pendingKey} 
              onCellClick={handleCellClick}
              selectedCell={selectedCell}
              disabled={!mayPlacePieces || busy}
            />
          </div>
        </section>

        <section className="panel">
          <h3 className="panel-title">格子上鎖定（刪除顏色重判連線）</h3>
          <div className="lock-list">
            {cellLockEntries.map((row) => (
              <div key={`${row.cell}-${row.teamId}`} className="lock-row">
                <span className="lock-row-label">格 {row.cell + 1} · {row.teamName}</span>
                <button
                  type="button"
                  className="link danger"
                  onClick={() => deleteCellLineScore(row.cell, row.teamId)}
                  disabled={busy}
                >
                  刪除
                </button>
              </div>
            ))}
            {!cellLockEntries.length && <div className="muted">目前無連線鎖定</div>}
          </div>
        </section>

        {/* 隊伍選擇彈窗 */}
        {showTeamSelector && (
          <div className="team-selector-overlay" onClick={() => { setShowTeamSelector(false); setSelectedCell(null); }}>
            <div className="team-selector-panel" onClick={(e) => e.stopPropagation()}>
              <div className="team-selector-title">選擇隊伍（格子 {selectedCell != null ? selectedCell + 1 : ''}）</div>
              {selectorBothLocked && (
                <div className="team-selector-hint">此格兩隊皆已鎖定（連線得分），無法再登記第三隊。</div>
              )}
              {!selectorBothLocked && (
                <div className="team-selector-hint">已佔領此格的隊伍不會列出；未鎖定的格子可被擠掉，連線鎖定的格子無法佔領。</div>
              )}
              <div className="team-selector-chips">
                {teamsSelectable.map(t => (
                  <button 
                    key={t.TEAM_ID}
                    className="team-selector-chip"
                    style={{ '--clr': t.COLOR_HEX || '#444' }}
                    onClick={() => handleTeamSelect(t.TEAM_ID)} 
                    disabled={busy}
                    title={t.TEAM_NAME}>
                    <span className="dot" style={{ background: t.COLOR_HEX || '#444' }} />
                    <span className="chip-text">{t.TEAM_NAME}</span>
                  </button>
                ))}
              </div>
              <button className="team-selector-cancel" onClick={() => { setShowTeamSelector(false); setSelectedCell(null); }}>取消</button>
            </div>
          </div>
        )}

        {(msg) && <div className="warn">{msg}</div>}

        {/* 明細 */}
        <section className="panel">
          <button className="collapse" onClick={() => setOpenPlays(v => !v)} aria-expanded={openPlays}>
            明細（可刪除誤傳）{openPlays ? '▲' : '▼'}
          </button>
          {openPlays && (
            <div className="table-wrap">
              <table>
                <thead><tr><th>ID</th><th>時間(UTC)</th><th>隊伍</th><th>格</th><th></th></tr></thead>
                <tbody>
                  {plays.map(p => (
                    <tr key={p.PLAY_ID}>
                      <td>{p.PLAY_ID}</td>
                      <td>{new Date(p.CREATED_AT).toISOString().slice(0,19).replace('T',' ')}</td>
                      <td>{p.TEAM_NAME}</td>
                      <td>{p.CELL + 1}</td>
                      <td><button className="link danger" onClick={() => remove(p.PLAY_ID)} disabled={busy}>刪除</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div style={{ height: '80px' }} />
      </main>


      {showStartModal && (
        <div className="team-selector-overlay" onClick={() => !busy && setShowStartModal(false)}>
          <div className="start-game-panel" onClick={(e) => e.stopPropagation()}>
            <div className="start-game-title">開始新局</div>
            <p className="start-game-hint">確認後將清除舊資料並開始計時。</p>
            <div className="start-row">
              <span className="lbl">時長</span>
              {[5, 10, 30, 60].map((m) => (
                <button key={m} type="button" className="preset" onClick={() => setDurationMinutes(m)} disabled={busy}>{m} 分</button>
              ))}
              <input className="dur-input" type="number" min={1} max={2880} value={durationMinutes} onChange={(e) => setDurationMinutes(Math.max(1, Number(e.target.value) || 1))} disabled={busy} />
            </div>
            <div className="start-row">
              <span className="lbl">連線判定</span>
              <input className="dur-input" type="number" min={0.1} step={0.1} max={86400} value={claimDecisionSecStr} onChange={(e) => setClaimDecisionSecStr(e.target.value)} disabled={busy} />
              <span className="lbl">秒</span>
            </div>
            <div className="start-row">
              <span className="lbl">格子上限</span>
              <button type="button" className={`preset ${cellCapacity === 1 ? 'active' : ''}`} onClick={() => setCellCapacity(1)} disabled={busy}>每格 1 隊</button>
              <button type="button" className={`preset ${cellCapacity === 2 ? 'active' : ''}`} onClick={() => setCellCapacity(2)} disabled={busy}>每格 2 隊</button>
            </div>
            <div className="start-row">
              <span className="lbl">參加隊伍</span>
              <button type="button" className="ghost tiny" onClick={selectAllParticipating} disabled={busy}>全選</button>
              <button type="button" className="ghost tiny" onClick={clearAllParticipating} disabled={busy}>全清</button>
            </div>
            <div className="part-wrap">
              {teamsAll.map((t) => (
                <label key={t.TEAM_ID} className="part-chip">
                  <input type="checkbox" checked={participatingTeamIds.includes(t.TEAM_ID)} onChange={() => toggleParticipating(t.TEAM_ID)} disabled={busy} />
                  <span className="part-name">{t.TEAM_NAME}</span>
                </label>
              ))}
            </div>
            <div className="start-game-actions">
              <button type="button" className="ghost" onClick={() => setShowStartModal(false)} disabled={busy}>取消</button>
              <button type="button" className="submit" onClick={confirmStartGame} disabled={busy}>確認開始</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== 子元件與樣式（原樣保留） ===== */
function BigClock({ timer }){
  const paused = timer?.paused;
  const sec = Math.max(0, Math.ceil((timer?.remainingMs || 0) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const text = h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  return (
    <div className="bigclock">
      {paused && <span className="paused-pill">已暫停</span>}
      <span className="clock">{text}</span>
    </div>
  );
}
function MiniCountdown({ claim, team }){
  const sec = Math.ceil((claim.left || 0) / 1000);
  const { dir, cellsLabel } = describePendingLine(claim.lineCode);
  return (
    <div className="mini-countdown">
      <span className="dot" style={{ background: team?.COLOR_HEX }} />
      <span className="mini-meta">
        <b>{team?.TEAM_NAME}</b>
        <span className="mini-lineinfo">{dir}{cellsLabel ? ` · 格${cellsLabel}` : ''}</span>
      </span>
      <span className="time">{Math.floor(sec/60)}:{String(sec%60).padStart(2,'0')}</span>
    </div>
  );
}

/* Grid：固定使用完整 teams 清單，並提供 icon 失效時的顏色方塊保底 */
function Grid({ occupancy = [], cellLocks = [], teams = [], pendingKey = new Set(), onCellClick, selectedCell, disabled = false }){
  const map = {};
  for (const t of teams) { map[t.TEAM_ID] = t; map[String(t.TEAM_ID)] = t; }
  const N = 4, cellPct = 100 / N;
  return (
    <div className="grid small">
      {Array.from({ length: 16 }).map((_, i) => (
        (() => {
          const display = orderCellBadges(occupancy[i], cellLocks[i]);
          const lockIds = Array.isArray(cellLocks[i]) ? [...new Set(cellLocks[i])] : [];
          const lockSet = new Set(lockIds.map((id) => Number(id)));
          const hasLock = (x) => lockSet.has(Number(x));
          const bothLocked = display.length === 2 && display.every((tid) => hasLock(tid));
          const clickDisabled = disabled || bothLocked;
          return (
        <div 
          key={i} 
          className={`cell ${bothLocked ? 'cell-both-locked' : ''} ${selectedCell === i ? 'cell-selected' : ''} ${clickDisabled ? 'cell-disabled' : ''}`}
          style={{
            position:'absolute',
            left:`${(i%N)*cellPct}%`, top:`${Math.floor(i/N)*cellPct}%`,
            width:`${cellPct}%`, height:`${cellPct}%`,
            cursor: clickDisabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => !clickDisabled && onCellClick && onCellClick(i)}
        >
          {bothLocked ? <span className="cell-lock-ring" aria-hidden /> : null}
          <div className="cell-num">{i + 1}</div>
          <div className="badges">
            {display.map((tid, idx) => {
                const isFade = display.length === 2 && idx === 1 && !hasLock(tid);
                const t = map[tid] || map[String(tid)] || {};
                const src = t.__icon;
                const color = t.COLOR_HEX || '#bbb';
                const showLockDot = hasLock(tid) && !bothLocked;
                return (
                  <div key={`${tid}-${idx}`} className={`badge ${isFade ? 'badge-fade' : ''}`}>
                    {src
                      ? <img
                          src={src}
                          alt=""
                          draggable={false}
                          className={showLockDot ? 'badge-image-locked' : ''}
                        />
                      : <div style={{
                          width:'100%', height:'100%', borderRadius:'var(--badge-radius)',
                          background: color,
                          border: showLockDot ? '3px solid #f01616' : '2px solid rgba(0,0,0,.08)',
                          boxSizing: 'border-box',
                        }} />
                    }
                  </div>
                );
              })}
          </div>
        </div>
          );
        })()
      ))}

      {LINES.map((line, idx) => (
        <AdminLine key={idx} line={line} occupancy={occupancy} teamsMap={map} pendingKey={pendingKey} />
      ))}
    </div>
  );
}

function AdminLine({ line, occupancy, teamsMap, pendingKey }){
  const [a,b,c,d] = line;
  const A = (occupancy[a]||[]), B = (occupancy[b]||[]), C = (occupancy[c]||[]), D = (occupancy[d]||[]);
  const candidate = (A[0] && A[0]===B[0] && A[0]===C[0] && A[0]===D[0]) ? [A[0]] : [];
  const N = 4;
  const center = i => ({ x:(i%N)+0.5, y:Math.floor(i/N)+0.5 });
  const p1 = center(a), p2 = center(d);
  const x1=(p1.x/N)*100, y1=(p1.y/N)*100, x2=(p2.x/N)*100, y2=(p2.y/N)*100;
  const dx=x2-x1, dy=y2-y1, len=Math.hypot(dx,dy), ang=Math.atan2(dy,dx)*180/Math.PI;
  const baseStyle = { left:`${x1}%`, top:`${y1}%`, width:`${len}%`, transform:`rotate(${ang}deg)` };
  const key = line.join('-');

  return (
    <>
      {candidate.map(teamId => {
        const color = (teamsMap[teamId] || teamsMap[String(teamId)] || {})?.COLOR_HEX || '#fff';
        const isPending = pendingKey.has(`${key}|${teamId}`);
        return (
          <div key={`pending-${teamId}`} className={`line ${isPending ? 'pending' : ''}`} style={baseStyle}>
            <div className="stroke-border" />
            <div className="stroke-color" style={{ '--clr': color }} />
          </div>
        );
      })}
    </>
  );
}

const css = `
:root { --safe-top: env(safe-area-inset-top, 0px); --safe-bottom: env(safe-area-inset-bottom, 0px); --badge-size: 65%; --badge-gap: 0%; --badge-radius: 8%;
  /* 雙鎖格外層紅框位置／線寬：請改此四邊 inset 與 border-width、color（本檔 css 字串頂部 :root） */
  --cell-lock-ring-inset-top: 2%;
  --cell-lock-ring-inset-right: 2%;
  --cell-lock-ring-inset-bottom: 2%;
  --cell-lock-ring-inset-left: 2%;
  --cell-lock-ring-border-width: 5px;
  --cell-lock-ring-color: #e50914;
}
* { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
html, body, #root { height: 100%; }
body { margin: 0; font-family: system-ui, -apple-system, "PingFang TC", "Microsoft JhengHei", sans-serif; color: #1c1c1c; }
button { font-family: inherit; }
.admin-m-wrap { display: grid; grid-template-rows: auto auto auto 1fr auto; height: 100dvh; background: #faf7f4; }

/* 勝利提示 */
.admin-victory{ position:fixed; inset:0; display:flex; align-items:center; justify-content:center; z-index:60; pointer-events:none; }
.admin-victory-panel{ background:#000; color:#fff; border-radius:16px; padding:20px 28px; box-shadow:0 10px 40px rgba(0,0,0,.35); }
.admin-victory-title{ font-size: clamp(22px, 4.8vw, 44px); font-weight:900; letter-spacing:.06em; }

/* 頂部工具列 */
.topbar { position: sticky; top: 0; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: calc(10px + var(--safe-top)) 12px 10px 12px; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); z-index: 10; }
.topbar .title { font-weight: 900; letter-spacing: .06em; }
.topbar .actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.timer-actions .ghost { font-size: 13px; padding: 8px 10px; }
.match-panel{ padding:10px 12px 12px; background:#fff; border-bottom:1px solid rgba(0,0,0,.06); }
.match-panel-head{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
.match-panel-title{ font-weight:900; font-size:15px; }
.match-status{ font-size:12px; font-weight:800; padding:4px 10px; border-radius:999px; background:#eee; color:#555; }
.match-status.on{ background:#e8f5e9; color:#1b5e20; }
.match-meta{ display:flex; flex-wrap:wrap; gap:12px; font-size:13px; font-weight:700; color:#555; margin-bottom:8px; }
.match-teams{ display:flex; flex-wrap:wrap; gap:6px 8px; }
.match-team-chip{ display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; background:#f5f5f5; font-size:13px; font-weight:800; }
.match-dot{ width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.start-game-panel{ background:#fff; border-radius:16px; padding:20px; max-width:420px; width:92vw; max-height:85vh; overflow:auto; box-shadow:0 12px 40px rgba(0,0,0,.2); }
.start-game-title{ font-weight:900; font-size:18px; margin-bottom:6px; }
.start-game-hint{ font-size:13px; color:#666; margin:0 0 12px; }
.start-game-actions{ display:flex; gap:10px; justify-content:flex-end; margin-top:16px; }
.start-bar{ padding: 8px 12px 4px; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); }
.start-row{ display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-bottom:8px; }
.start-bar .lbl{ font-weight:800; font-size:13px; color:#555; }
.start-bar .preset, .start-game-panel .preset{ padding:6px 10px; border-radius:8px; border:1px solid rgba(0,0,0,.12); background:#fafafa; font-weight:800; font-size:13px; cursor:pointer; }
.start-game-panel .preset.active{ background:#111; color:#fff; border-color:#111; }
.start-bar .dur-input{ width:72px; padding:6px 8px; border-radius:8px; border:1px solid rgba(0,0,0,.12); font-weight:800; }
.start-bar .ghost.tiny{ padding:4px 8px; font-size:12px; }
.part-wrap{ display:flex; flex-wrap:wrap; gap:6px 10px; }
.part-chip{ display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:700; cursor:pointer; user-select:none; }
.part-chip input{ accent-color:#111; }
.part-name{ max-width:120px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.group-form{ display:flex; flex-wrap:wrap; gap:8px; margin-bottom:10px; align-items:center; }
.group-form .g-inp{ flex:1; min-width:120px; padding:8px 10px; border-radius:10px; border:1px solid rgba(0,0,0,.12); font-weight:700; }
.lock-add-form{ display:flex; flex-wrap:wrap; gap:8px; margin-bottom:10px; align-items:center; }
.lock-add-form .lock-inp{ flex:1; min-width:100px; padding:8px 10px; border-radius:10px; border:1px solid rgba(0,0,0,.12); font-weight:700; }
.lock-list{ display:flex; flex-direction:column; gap:8px; }
.lock-row{ display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:8px; padding:10px 12px; background:#fafafa; border:1px solid #eee; border-radius:10px; font-size:14px; }
.lock-row-label{ font-weight:800; }
.group-team-pick{ display:flex; flex-wrap:wrap; gap:6px 10px; margin-bottom:10px; }
.group-list{ display:flex; flex-direction:column; gap:8px; }
.group-row{ display:flex; flex-wrap:wrap; align-items:flex-start; justify-content:space-between; gap:10px; padding:10px 12px; background:#fafafa; border:1px solid #eee; border-radius:10px; font-size:14px; }
.group-row-main{ flex:1; min-width:0; }
.g-line1{ display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-bottom:4px; }
.group-row .g-slug{ font-weight:900; font-family: ui-monospace, monospace; font-size:15px; }
.g-dn{ font-weight:800; color:#444; font-size:13px; }
.g-teams-line{ font-size:13px; line-height:1.45; color:#333; }
.g-teams-label{ font-weight:900; color:#666; margin-right:6px; }
.g-teams-names{ font-weight:700; word-break:break-all; }
.group-row-actions{ display:flex; flex-wrap:wrap; align-items:center; gap:6px; flex-shrink:0; }
.muted{ color:#888; font-weight:700; font-size:13px; padding:8px 0; }

.topbar .ghost { padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 800; }
.topbar .danger { padding: 10px 12px; border-radius: 10px; border: 1px solid #c21; background: #fef2f2; color: #c21; font-weight: 900; }

/* 時鐘與倒數 */
.clock-wrap{ padding: 6px 12px 0; }
.bigclock{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; margin:2px 0 6px; }
.bigclock .paused-pill{ font-size:12px; font-weight:900; color:#c21; padding:2px 8px; border-radius:8px; background:#fee; border:1px solid #fcc; }
.bigclock .clock{ color:#111; font-weight:900; letter-spacing:.08em; font-size:24px; }
.game-ended-banner{ display:flex; justify-content:center; margin:4px 0; }
.game-ended-text{ padding:6px 12px; background:#fee; color:#c21; border:1px solid #fcc; border-radius:8px; font-weight:900; font-size:14px; }
.countdown-stack{ display:flex; flex-direction:column; gap:6px; }
.mini-countdown{ display:flex; align-items:center; gap:8px; padding:6px 10px; background:#fff; border:1px solid #eee; border-radius:10px; font-weight:800; letter-spacing:.02em; font-size:13px; line-height:1.25; }
.mini-countdown .dot{ width:10px; height:10px; border-radius:999px; display:inline-block; flex-shrink:0; }
.mini-countdown .mini-meta{ display:flex; flex-direction:column; align-items:flex-start; gap:1px; min-width:0; flex:1; }
.mini-countdown .mini-lineinfo{ font-size:11px; font-weight:700; color:#555; word-break:break-all; }
.mini-countdown .time{ font-variant-numeric: tabular-nums; margin-left:auto; flex-shrink:0; }

/* 重置列 */
.toolbar-reset{ display:flex; gap:8px; padding:8px 12px; border-top:1px dashed #eee; border-bottom:1px dashed #eee; background:#fff; }
.toolbar-reset button{ padding:10px 12px; border-radius:10px; border:1px solid rgba(0,0,0,.12); background:#fff; font-weight:800; }
.toolbar-reset .danger{ border-color:#c21; color:#c21; background:#fef2f2; }

/* 面板與盤面 */
.content { overflow: auto; padding: 12px; }
.panel { background: #fff; border-radius: 14px; box-shadow: 0 4px 12px rgba(0,0,0,.06); padding: 12px; margin-bottom: 12px; }
.panel-title { margin: 0 0 8px 0; font-size: 16px; font-weight: 900; }

/* 分數：單列橫排可橫向捲動 */
.score-board.score-board-row{
  display:flex; flex-direction:row; flex-wrap:nowrap; align-items:stretch;
  gap:8px; overflow-x:auto; padding-bottom:4px; -webkit-overflow-scrolling:touch;
}
.score-chip-inline{
  flex:0 0 auto; display:inline-flex; align-items:center; gap:6px;
  padding:8px 10px; background:#fafafa; border:1px solid #eee; border-radius:999px;
  max-width:min(200px, 42vw); min-width:0;
}
.score-chip-inline.top-score{ background:#fff9e6; border-color:#f6c700; }
.sc-rank{ font-weight:900; font-size:12px; color:#888; flex-shrink:0; }
.sc-dot{ width:10px; height:10px; border-radius:50%; border:1px solid rgba(0,0,0,.12); flex-shrink:0; }
.sc-name{ font-weight:800; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; }
.sc-val{ font-weight:900; font-size:14px; color:#111; font-variant-numeric:tabular-nums; flex-shrink:0; margin-left:2px; }
.score-empty{ padding:12px; text-align:center; color:#888; font-weight:800; flex:1 0 100%; }
.game-winner-announce{ margin-top:12px; padding:12px; background:#e6f7ff; border:2px solid #38b6ff; border-radius:10px; text-align:center; font-weight:900; font-size:16px; color:#111; }
.game-winner-announce strong{ color:#38b6ff; }

/* 盤面預覽 */
.board { width: 100%; aspect-ratio: 1 / 1; border: 1px solid #eee; border-radius: 12px; padding: 10px; background: #fafafa; }
.grid.small { width: 100%; height: 100%; position: relative; background: #fafafa;
  background-image: linear-gradient(to right, rgba(0,0,0,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,.06) 1px, transparent 1px);
  background-size: calc(100%/4) calc(100%/4); border-radius: 12px; overflow: hidden; }
.grid.small .cell { position:absolute; display:grid; place-items:center; background:transparent !important; z-index:5; }
.grid.small .cell-lock-ring{
  position:absolute; z-index:9; pointer-events:none; box-sizing:border-box;
  top: var(--cell-lock-ring-inset-top); right: var(--cell-lock-ring-inset-right);
  bottom: var(--cell-lock-ring-inset-bottom); left: var(--cell-lock-ring-inset-left);
  border: var(--cell-lock-ring-border-width) solid var(--cell-lock-ring-color);
  border-radius: 10px;
}
.grid.small .cell-both-locked::after{ content:''; position:absolute; inset:0; background:rgba(0,0,0,.06); border-radius:12px; pointer-events:none; }
.grid.small .cell-num{ position:absolute; left:6%; top:4%; font-size: clamp(10px, 2.8vw, 14px); color:#888; font-weight:800; user-select:none; pointer-events:none; }
.grid.small .badges{ border:none; position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap: var(--badge-gap); margin: 0 auto; text-align:center; }
.grid.small .badge { position:relative; width: var(--badge-size); aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; border-radius: var(--badge-radius); border:0 !important; box-shadow:none !important; outline:none !important; background:transparent !important; margin:0 auto; }
.grid.small .badge img{ max-width:100%; max-height:100%; object-fit:contain; display:block;border: none ;}
.grid.small .badge img.badge-image-locked{
  outline: 4px solid #f01616 !important;
  outline-offset: 3px;
  border-radius:12%;
  box-sizing:border-box;
}
@keyframes fading { 0% {opacity:.85} 50% {opacity:.55} 100% {opacity:.85} }
.grid.small .badge-fade { animation: fading 1.1s infinite; }
.grid.small .cell-selected { background: rgba(59, 130, 246, 0.15) !important; border: 2px solid #3b82f6 !important; }
.grid.small .cell-disabled { opacity: 0.5; }

/* 線條（在格子下層，避免擋住圖示） */
.line { position:absolute; height:0; transform-origin:0 0; pointer-events:none; mix-blend-mode:normal; z-index:2; }
.line .stroke-border{ position:absolute; left:0; top:-3.0%; height:6.0%; width:100%; background:#fff; border-radius:999px; }
.line .stroke-color{ position:absolute; left:0; top:-2.2%; height:4.4%; width:100%; background:var(--clr,#fff); border-radius:999px; opacity:1; }
.line.pending .stroke-border{ animation: dashA 0.65s linear infinite; }
.line.pending .stroke-color{ animation: thickenA 0.65s ease-in-out infinite alternate, dashA 0.65s linear infinite; }
@keyframes dashA { 0%{opacity:.60} 50%{opacity:1} 100%{opacity:.60} }
@keyframes thickenA { from{height:4.2%} to{height:4.9%} }

/* 隊伍籌碼、鍵盤 */
.chips { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 2px; }
.chip { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 900; }
.chip .dot { width: 12px; height: 12px; border-radius: 50%; border: 1px solid rgba(0,0,0,.25); }
.chip.active { background: var(--clr); color: #fff; border-color: var(--clr); }
.chip.active .dot { border-color: rgba(255,255,255,.9); }

.keypad { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.num { height: 54px; border-radius: 12px; border: 1px solid rgba(0,0,0,.10); background: #fff; font-size: 18px; font-weight: 900; letter-spacing: .06em; }
.num:active { transform: translateY(1px); }
.num.active { border-color: #333; box-shadow: 0 0 0 2px #333 inset; }

/* 其他 */
.warn { color:#d00; font-weight: 900; margin: 6px 2px 10px; }
.collapse { width: 100%; text-align: left; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(0,0,0,.10); background: #fafafa; font-weight: 800; }
.table-wrap { margin-top: 10px; border: 1px solid #eee; border-radius: 10px; overflow: auto; max-height: 45vh; }
table { width: 100%; border-collapse: collapse; background: #fff; }
th, td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; white-space: nowrap; }
.link { background: none; border: none; color: #0a66c2; font-weight: 800; }
.link.danger { color: #c21; }

/* 隊伍選擇彈窗 */
.team-selector-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
.team-selector-panel { background: #fff; border-radius: 16px; padding: 20px; max-width: 90vw; width: 400px; box-shadow: 0 10px 40px rgba(0,0,0,.3); }
.team-selector-title { font-size: 18px; font-weight: 900; margin-bottom: 8px; text-align: center; }
.team-selector-hint { font-size: 12px; font-weight: 700; color: #555; line-height: 1.4; margin-bottom: 12px; text-align: center; }
.team-selector-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.team-selector-chip { display: inline-flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 900; cursor: pointer; transition: all 0.2s; }
.team-selector-chip:hover:not(:disabled) { background: var(--clr); color: #fff; border-color: var(--clr); }
.team-selector-chip:disabled { cursor: not-allowed; }
.team-selector-chip .dot { width: 12px; height: 12px; border-radius: 50%; border: 1px solid rgba(0,0,0,.25); }
.team-selector-chip:hover:not(:disabled) .dot { border-color: rgba(255,255,255,.9); }
.team-selector-cancel { width: 100%; padding: 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.12); background: #f5f5f5; font-weight: 800; cursor: pointer; }
.team-selector-cancel:hover { background: #e5e5e5; }

@media (min-width: 768px) {
  .content { max-width: 820px; margin: 0 auto; }
  .board { padding: 14px; }
  .num { height: 64px; font-size: 20px; }
}
`;
